/**
 * Client-side LexoRank calculator for Category.priority.
 * Mirrors server-side `App\Ship\Helpers\PositionLexicographicCalculator` in PHP.
 *
 * Alphabet: 0-9, A-Z, a-z (base62, ascending code points).
 * Guarantees: returned priority never ends with the first or last alphabet
 * character, so callers can always produce a lower/higher neighbour.
 */

const ALPHABET =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const LAST_INDEX = ALPHABET.length - 1;
const DEFAULT_INDEX = 30; // "U"
const DEFAULT_CHAR = ALPHABET.charAt(DEFAULT_INDEX);

/** Safe alphabet lookup. Unknown characters fall back to `LAST_INDEX` so that
 *  callers never crash on malformed priorities from the server — the value
 *  will look "large", and downstream `normalize`/`increment` handle it. */
const alphabetIndex = (ch: string): number => {
  const i = ALPHABET.indexOf(ch);
  return i === -1 ? LAST_INDEX : i;
};

/** Safe alphabet lookup. `noUncheckedIndexedAccess` forces string|undefined for
 *  bracket access; `.charAt(i)` always returns a string ("" for out of bounds).
 *  All callers here pass indices computed within the alphabet range. */
const alphabetChar = (i: number): string => ALPHABET.charAt(i);

/** Non-empty string with only alphabet characters, last char is not first/last. */
const VALID_RE = new RegExp(
  `^[${ALPHABET}]*[${ALPHABET.slice(1, -1)}]$`,
);

export const isValidPriority = (v: string | null | undefined): boolean => {
  if (v === null || v === undefined) return true;
  return typeof v === "string" && VALID_RE.test(v);
};

/** "" is treated as "no priority yet" — PHP `isValidPosition` rejects it. */
const normalizeInput = (v: string | null | undefined): string | null =>
  v === null || v === undefined || v === "" ? null : v;

const normalize = (position: string): string => {
  const last = position.charAt(position.length - 1);
  const i = alphabetIndex(last);
  if (i === 0 || i === LAST_INDEX) return position + DEFAULT_CHAR;
  return position;
};

const increment = (position: string): string => {
  let charIndex = 0;
  let charAlphabetIndex: number;
  do {
    charAlphabetIndex = alphabetIndex(position.charAt(charIndex));
    charIndex++;
  } while (charAlphabetIndex === LAST_INDEX);
  // Увеличиваем — округляем дробную часть вверх, чтобы двигаться в сторону LAST_INDEX.
  const nextIndex = Math.ceil((charAlphabetIndex + LAST_INDEX) / 2);
  return normalize(position.slice(0, charIndex - 1) + alphabetChar(nextIndex));
};

const decrement = (position: string): string => {
  let charIndex = 0;
  let charAlphabetIndex: number;
  do {
    charAlphabetIndex = alphabetIndex(position.charAt(charIndex));
    charIndex++;
  } while (charAlphabetIndex === 0);
  const nextIndex = Math.floor(charAlphabetIndex / 2);
  return normalize(position.slice(0, charIndex - 1) + alphabetChar(nextIndex));
};

const between = (prev: string, next: string): string => {
  if (prev >= next) {
    throw new Error(
      `calculatePriority: prev must be < next, got prev="${prev}" next="${next}"`,
    );
  }
  const maxLen = Math.min(prev.length, next.length);
  for (let i = 0; i < maxLen; i++) {
    const p = alphabetIndex(prev.charAt(i));
    const n = alphabetIndex(next.charAt(i));
    if (n - p > 1) {
      const mid = Math.floor((p + n) / 2);
      return normalize(prev.slice(0, i) + alphabetChar(mid));
    }
  }
  // strings too close — extend
  if (prev.length < next.length) {
    return prev + decrement(next.slice(prev.length));
  }
  if (prev.length > next.length) {
    return prev + increment(prev.slice(next.length));
  }
  return prev + DEFAULT_CHAR;
};

/**
 * Calculate a new priority between two neighbours.
 * null prev/next means "no neighbour on that side" (edge of list).
 */
export const calculatePriority = (
  prevPriority: string | null | undefined,
  nextPriority: string | null | undefined,
): string => {
  const prev = normalizeInput(prevPriority);
  const next = normalizeInput(nextPriority);

  if (!isValidPriority(prev)) {
    throw new Error(`calculatePriority: invalid prev="${prev}"`);
  }
  if (!isValidPriority(next)) {
    throw new Error(`calculatePriority: invalid next="${next}"`);
  }

  if (prev !== null && next === null) return increment(prev);
  if (prev !== null && next !== null) return between(prev, next);
  if (prev === null && next !== null) return decrement(next);
  return DEFAULT_CHAR;
};

/**
 * Lexicographic comparator for Category.priority values.
 * Pure code-point comparison matches base62 order (0 < 9 < A < Z < a < z).
 * Empty string sorts first (treated as "not positioned").
 */
export const comparePriority = (
  a: string | null | undefined,
  b: string | null | undefined,
): number => {
  const sa = a ?? "";
  const sb = b ?? "";
  if (sa < sb) return -1;
  if (sa > sb) return 1;
  return 0;
};
